// ADR-0051 / V101-1-2: the connector manifest. One declarative entry per
// connector kind; routes, the runner, the scheduler, the blueprint and the
// UI read the registry instead of hand-maintained kind lists (V101-1-4).
//
// Manifests are reviewed code, never DB rows: they carry security
// properties (scopes, hosts, data boundary) that must not change at runtime.
// Collector implementations are registered separately by kind.
//
// Pure data + pure validation — no runtime imports (check-imports).
/** Families whose data may contain model inputs/outputs: bodies are never collected (D-CI-4). */
export const BODY_SENSITIVE_FAMILIES = [
    'ai_provider',
    'ai_gateway',
    'vector_db',
    'inference_runtime',
    'kubernetes',
];
/** G-1l: families that sit on the customer's business records; they must declare neverCollects business_data. */
export const BUSINESS_DATA_FAMILIES = ['saas', 'cloud', 'vector_db'];
/**
 * Setup-page sections in display order (G-1g). A group missing here is still
 * shown, after these, in registry order; ids are the stable DOM anchors.
 */
export const CONNECTOR_GROUPS = Object.freeze([
    { id: 'identity', title: 'Identity provider' },
    { id: 'ai-discovery', title: 'AI app discovery' },
    { id: 'cloud', title: 'Cloud account' },
    { id: 'vulnerability', title: 'Vulnerability scanning' },
    { id: 'productivity', title: 'Productivity suite' },
    { id: 'ai-provider', title: 'AI provider' },
    { id: 'ai-gateway', title: 'AI gateway' },
    { id: 'vector-db', title: 'Vector database' },
    { id: 'observability', title: 'Observability' },
    { id: 'data-platform', title: 'Data platform' },
    { id: 'ml-registry', title: 'ML registry' },
    { id: 'data-governance', title: 'Data governance' },
    { id: 'source-control', title: 'Source control' },
    { id: 'iac', title: 'Infrastructure as code' },
    { id: 'collaboration', title: 'Collaboration' },
    { id: 'ticketing', title: 'Ticketing' },
    { id: 'inference-runtime', title: 'Inference runtime' },
    { id: 'container-platform', title: 'Container platform' },
    { id: 'uploads', title: 'Uploads and CI' },
]);
/** Group titles ordered by CONNECTOR_GROUPS; unknown titles follow in first-seen order. */
export function orderConnectorGroups(titles) {
    const seen = [...new Set(titles)];
    const known = CONNECTOR_GROUPS.filter((g) => seen.includes(g.title));
    const rest = seen.filter((t) => !CONNECTOR_GROUPS.some((g) => g.title === t))
        .map((title) => ({ id: title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''), title }));
    return [...known, ...rest];
}
/** Families whose software customers run on their own domains. */
export const SELF_HOSTABLE_FAMILIES = ['ml_registry', 'vector_db', 'inference_runtime', 'kubernetes', 'observability'];
const ISO_DURATION = /^P(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?)?$/;
const HOST_GLOB = /^(\*\.)?[a-z0-9-]+(\.[a-z0-9-]+)+$/;
/**
 * V104-C-4: stands for one region / location DNS label in a fixed host
 * (`config.{region}.amazonaws.com`, `{region}-aiplatform.googleapis.com`). It never
 * covers the registrable domain, so a service host stays a service host.
 */
export const HOST_REGION_TOKEN = '{region}';
/**
 * V104-C-4 (review I-1): shared cloud parents where `*.` would admit every service
 * the provider runs. Manifests list the service hosts their collectors call instead.
 */
export const BROAD_HOST_PARENTS = ['amazonaws.com', 'googleapis.com'];
/** A hostname, a `*.domain` glob, either with at most one `{region}` label. */
function isHostGlob(h) {
    const tokens = h.split(HOST_REGION_TOKEN).length - 1;
    if (tokens > 1)
        return false;
    if (tokens === 1) {
        const labels = h.split('.');
        const i = labels.findIndex((l) => l.includes(HOST_REGION_TOKEN));
        if (i >= labels.length - 2)
            return false;
        // C-7 L-3: the token is a whole label or a prefix with a fixed suffix (`{region}-aiplatform`).
        // Inside a label (`s{region}`) it could stand in for part of a service name (`sts`).
        if (!/^\{region\}(-[a-z0-9-]+)?$/.test(labels[i]))
            return false;
    }
    return HOST_GLOB.test(h.replace(HOST_REGION_TOKEN, 'region'));
}
/** The parent a bare wildcard covers (`*.amazonaws.com`, `*.{region}.amazonaws.com`), else null. */
export function broadHostParent(h) {
    const core = h.replace(/^\*\./, '').replace(`${HOST_REGION_TOKEN}.`, '');
    return core !== h && BROAD_HOST_PARENTS.includes(core) ? core : null;
}
/**
 * ADR-0051 §6 invariants. Returns human-readable violations; empty = valid.
 * The conformance test runs this over every registered manifest.
 */
/** ADR-0102 §6: scopes a connection granted at `grantedVersion` is missing (empty when current). */
export function scopesAddedSince(m, grantedVersion) {
    return [...new Set((m.scopeChanges ?? []).filter((c) => c.version > grantedVersion).flatMap((c) => c.added))].sort();
}
export function validateManifest(m, ctx) {
    const v = [];
    const at = (msg) => v.push(`${m.kind}: ${msg}`);
    if (!/^[a-z][a-z0-9_]{1,40}$/.test(m.kind))
        at('kind must be lower_snake_case');
    if (!Number.isInteger(m.manifestVersion) || m.manifestVersion < 1)
        at('manifestVersion must be a positive integer');
    if (m.auth.length === 0)
        at('at least one auth entry (use kind "none" for uploads)');
    // Scopes: read-only, or declared.
    const declared = new Set(m.privilege?.beyondReadOnly ?? []);
    for (const a of m.auth) {
        if (a.lintProfile === 'none')
            continue;
        for (const s of ctx.lint(a.lintProfile, a.scopes)) {
            if (!declared.has(s))
                at(`scope "${s}" fails ${a.lintProfile} lint and is not declared in privilege.beyondReadOnly`);
        }
    }
    for (const set of m.form?.together ?? []) {
        for (const k of set)
            if (!m.form?.fields.some((x) => x.key === k))
                at(`form.together names "${k}", which is not a form field`);
        if (set.length < 2)
            at('form.together sets need at least two fields');
    }
    if (m.privilege) {
        if (m.privilege.beyondReadOnly.length === 0)
            at('privilege declared with no beyondReadOnly scopes');
        if (!m.privilege.reason.trim() || !m.privilege.vendorLimitation.trim())
            at('privilege needs reason and vendorLimitation');
        if (!(m.privilege.revalidateDays > 0 && m.privilege.revalidateDays <= 90))
            at('privilege.revalidateDays must be 1..90');
        const only = m.privilege.onlyWithFields ?? [];
        for (const k of only)
            if (!m.form?.fields.some((x) => x.key === k))
                at(`privilege.onlyWithFields names "${k}", which is not a form field`);
        // The runner decides from config (no decryption), so at least one named field must be stored there.
        if (only.length && !only.some((k) => m.form?.fields.find((x) => x.key === k)?.store !== 'credential')) {
            at("privilege.onlyWithFields needs at least one field stored in config ('config' or 'both')");
        }
        const allScopes = new Set(m.auth.flatMap((a) => a.scopes));
        for (const s of m.privilege.beyondReadOnly)
            if (!allScopes.has(s))
                at(`privilege lists "${s}" which no auth entry requests`);
    }
    // Hosts: pinned, no bare wildcards.
    const globs = [...(m.hosts.fixed ?? []), ...(m.hosts.customerSupplied?.patterns ?? [])];
    for (const h of globs) {
        if (!isHostGlob(h))
            at(`host pattern "${h}" is not a hostname or *.domain glob`);
        const parent = broadHostParent(h);
        if (parent)
            at(`host pattern "${h}" admits every ${parent} service; list the service hosts this connector calls`);
    }
    const cs = m.hosts.customerSupplied;
    if (cs && cs.patterns.length === 0 && !cs.anyPublicHost && !cs.privateHosts) {
        at('customerSupplied host needs at least one pattern (or anyPublicHost / privateHosts for self-hosted software)');
    }
    if (cs?.privateHosts) {
        // V101-8-2: private hosts are reachable only from the customer collector.
        if (m.runnableIn.includes('worker'))
            at('privateHosts is not allowed on a kind a Worker can run (a Worker never calls a private host)');
        if (!m.runnableIn.includes('customer'))
            at('privateHosts requires runnableIn customer');
        if (cs.anyPublicHost)
            at('privateHosts and anyPublicHost are mutually exclusive');
        if (!SELF_HOSTABLE_FAMILIES.includes(m.family))
            at(`privateHosts is only for self-hostable families, not ${m.family}`);
    }
    // ADR-0055 §4: customer_collector rows are created by the collector ingest with auth kind collector_key.
    if (m.managedBy === 'customer_collector') {
        if (!m.runnableIn.includes('customer'))
            at('managedBy customer_collector requires runnableIn customer');
        if (!m.auth.some((a) => a.kind === 'collector_key'))
            at('managedBy customer_collector requires an auth entry of kind collector_key');
    }
    else if (m.runnableIn.length === 1 && m.runnableIn[0] === 'customer') {
        at('a customer-only kind must be managedBy customer_collector');
    }
    if (cs?.anyPublicHost && !SELF_HOSTABLE_FAMILIES.includes(m.family)) {
        at(`anyPublicHost is only for self-hostable families, not ${m.family}`);
    }
    if (m.runnableIn.includes('worker') && globs.length === 0 && !cs?.anyPublicHost && m.lifecycle === 'active') {
        at('a worker-run collector must declare the hosts it calls');
    }
    // Data boundary.
    if (BODY_SENSITIVE_FAMILIES.includes(m.family)) {
        for (const b of ['prompt_body', 'response_body']) {
            if (!m.dataBoundary.neverCollects.includes(b))
                at(`family ${m.family} must declare neverCollects ${b}`);
        }
    }
    if (!m.dataBoundary.neverCollects.includes('secrets'))
        at('neverCollects must include secrets');
    if (BUSINESS_DATA_FAMILIES.includes(m.family) && !m.dataBoundary.neverCollects.includes('business_data')) {
        at(`family ${m.family} must declare neverCollects business_data`);
    }
    // Connect form (G-1e) and list target (G-1h).
    if (m.form)
        for (const e of validateForm(m))
            at(e);
    const target = m.ui.target;
    if (target?.field && m.form && !m.form.fields.some((f) => f.key === target.field && f.store !== 'credential')) {
        at(`ui.target.field "${target.field}" is not a config field of the form`);
    }
    if (target?.display === 'tail4' && !target.noun)
        at('ui.target tail4 needs a noun');
    // Outputs, events, schedule.
    const checks = new Set(m.outputs.checks);
    if (checks.size !== m.outputs.checks.length)
        at('duplicate check keys');
    for (const d of m.outputs.deprecatedChecks ?? [])
        if (checks.has(d))
            at(`check "${d}" is both active and deprecated`);
    for (const [k, prods] of Object.entries(m.outputs.checkProducts ?? {})) {
        if (!checks.has(k))
            at(`checkProducts names unknown check "${k}"`);
        if (!prods.length)
            at(`checkProducts "${k}" lists no product`);
    }
    for (const e of m.events ?? []) {
        if (ctx.verifiers && !ctx.verifiers.has(e.verifier))
            at(`unknown event verifier "${e.verifier}"`);
        for (const k of e.triggersChecks)
            if (!checks.has(k))
                at(`event triggers unknown check "${k}"`);
    }
    if (m.runnableIn.length > 0 && m.lifecycle === 'active' && !m.schedule)
        at('a pull collector needs a schedule');
    if (m.schedule) {
        if (!ISO_DURATION.test(m.schedule.defaultCadence) || !ISO_DURATION.test(m.schedule.minCadence)) {
            at('schedule cadences must be ISO 8601 durations (e.g. P1D, PT6H)');
        }
    }
    if (m.captureShapes.includes('population') && !(m.outputs.populations?.length))
        at('population shape without populations');
    if (m.runner !== 'none' && !m.runnableIn.includes('worker'))
        at(`runner ${m.runner} requires runnableIn worker`);
    if (m.limits && !(m.limits.maxRequests > 0 && m.limits.requestsPerMinute > 0))
        at('limits must be positive');
    if (m.lifecycle === 'active' && m.runner !== 'none' && !m.limits)
        at('an active pull collector must declare limits (ADR-0052 §5)');
    if (m.lifecycle === 'planned' && (m.runner !== 'none' || m.blueprint))
        at('a planned kind has no runner and claims no blueprint slot');
    if (m.captureShapes.includes('bulk') && !(m.outputs.rollups?.length))
        at('bulk shape without rollups');
    // ADR-0102 §6: a scope bump names a version 2..manifestVersion (once) and scopes an auth entry requests.
    const requested = new Set(m.auth.flatMap((a) => a.scopes));
    const seenVersions = new Set();
    for (const ch of m.scopeChanges ?? []) {
        if (!Number.isInteger(ch.version) || ch.version < 2 || ch.version > m.manifestVersion)
            at(`scopeChanges version ${ch.version} must be 2..manifestVersion`);
        if (seenVersions.has(ch.version))
            at(`scopeChanges lists version ${ch.version} twice`);
        seenVersions.add(ch.version);
        if (!ch.added.length || !ch.reason.trim())
            at(`scopeChanges v${ch.version} needs added scopes and a reason`);
        for (const s of ch.added)
            if (!requested.has(s))
                at(`scopeChanges v${ch.version} adds "${s}", which no auth entry requests`);
    }
    return v;
}
const FORM_KEY = /^[a-z][a-z0-9_]{0,40}$/;
const RESERVED_FORM_KEYS = ['kind', 'name', 'confirm_privilege', 'display_name'];
function validateForm(m) {
    const v = [];
    const form = m.form;
    if (m.managedBy !== 'posture_connectors')
        v.push('a form is only for kinds created on the connections page (managedBy posture_connectors)');
    const keys = new Set();
    for (const f of form.fields) {
        if (!FORM_KEY.test(f.key) || RESERVED_FORM_KEYS.includes(f.key))
            v.push(`form field key "${f.key}" is not allowed`);
        if (keys.has(f.key))
            v.push(`duplicate form field "${f.key}"`);
        keys.add(f.key);
        if (!f.label.trim())
            v.push(`form field "${f.key}" needs a label`);
        const input = f.input ?? (f.store === 'config' ? 'text' : 'secret');
        if (input === 'select' && !f.options?.length)
            v.push(`select field "${f.key}" needs options`);
        if (input !== 'select' && f.options)
            v.push(`field "${f.key}" has options but is not a select`);
        if (input === 'list' && (!f.list || f.store !== 'config'))
            v.push(`list field "${f.key}" needs list bounds and store config`);
        if (input !== 'list' && f.list)
            v.push(`field "${f.key}" has list bounds but is not a list`);
        if (input === 'secret' && f.store === 'config')
            v.push(`secret field "${f.key}" must not be stored in config`);
        if (typeof f.validate === 'object') {
            try {
                new RegExp(f.validate.regex, f.validate.flags);
            }
            catch {
                v.push(`field "${f.key}" regex does not compile`);
            }
        }
        if ((f.validate === 'customer_host' || typeof f.validate === 'object' || input === 'select') && !f.message)
            v.push(`field "${f.key}" needs a message for its check`);
        if (f.validate === 'customer_host') {
            if (m.hosts.customerSupplied?.field !== f.key)
                v.push(`customer_host field "${f.key}" must be hosts.customerSupplied.field`);
            if (f.store !== 'config')
                v.push(`customer_host field "${f.key}" must be stored in config only`);
        }
        else if (f.host)
            v.push(`field "${f.key}" has host rules but is not customer_host`);
        for (const d of f.host?.deny ?? [])
            if (!HOST_GLOB.test(d.pattern))
                v.push(`deny pattern "${d.pattern}" is not a hostname or *.domain glob`);
        if (f.min !== undefined && f.max !== undefined && f.min > f.max)
            v.push(`field "${f.key}" min > max`);
    }
    const cs = m.hosts.customerSupplied;
    if (cs && !cs.privateHosts && !form.fields.some((f) => f.key === cs.field && f.validate === 'customer_host')) {
        v.push(`hosts.customerSupplied.field "${cs.field}" needs a customer_host form field`);
    }
    return v;
}
/**
 * Hostname glob match (`*.okta.com` matches `acme.okta.com`, not `okta.com`).
 * `{region}` matches exactly one DNS label (or the part of one it stands in).
 */
export function hostMatches(host, pattern) {
    const h = host.toLowerCase();
    const p = pattern.toLowerCase();
    if (p.includes(HOST_REGION_TOKEN)) {
        const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const wild = p.startsWith('*.');
        const [before, after] = (wild ? p.slice(2) : p).split(HOST_REGION_TOKEN);
        return new RegExp(`^${wild ? '(?:[a-z0-9-]+\\.)+' : ''}${esc(before)}[a-z0-9-]+${esc(after)}$`).test(h);
    }
    if (p.startsWith('*.')) {
        const suffix = p.slice(1);
        return h.endsWith(suffix) && h.length > suffix.length && !h.slice(0, -suffix.length).includes('/');
    }
    return h === p;
}
//# sourceMappingURL=manifest.js.map